import { api } from './client';
import type { ChangeRequest, ChangeRequestDecision, ChangeRequestStatus, Page } from './types';
import { UUID } from './types';

/** #47 (2026-06-12): per-column predicate filters (C-132). `entity_types`
 *  and `kinds` expand to repeated query keys (multi-select). */
export interface ChangeRequestsListFilters {
  status?: ChangeRequestStatus;
  entity_type?: string;          // legacy single-value callers (pending-CR queries)
  entity_types?: string[];       // #47 multi-select header filter
  kinds?: string[];              // #47: CREATE / UPDATE / DELETE / RESTORE
  /** #47; multi-select since C-132 Amendment 3 (repeated query keys). */
  requested_by_user_id?: UUID[];
  requested_from?: string;       // #47, YYYY-MM-DD inclusive
  requested_to?: string;         // #47, YYYY-MM-DD inclusive
  include_deleted?: boolean;
  /** P41: header sort, "key" or "-key" (server-side, across pages). */
  sort?: string;
}

/** §3j friendly labels for change_request.entity_type — the closed option
 *  set for the #47 Entity-type header filter (mirrors ENTITY_REGISTRY). */
export const CR_ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'agreements', label: 'Agreements' },
  { value: 'persons', label: 'Persons' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'insurance_policies', label: 'Insurance Policies' },
  { value: 'agreement_parties', label: 'Agreement Parties' },
  { value: 'account_parties', label: 'Account Parties' },
  { value: 'insurance_policy_parties', label: 'Policy Parties' },
  { value: 'insurance_policy_accounts', label: 'Policy ↔ Account links' },
  { value: 'agreement_documents', label: 'Agreement Documents (IAA)' },
  { value: 'agreement_interactions', label: 'Interactions' },
  { value: 'agreement_tasks', label: 'Tasks' },
  { value: 'completeness_rules', label: 'Completeness Rules' },
  { value: 'compliance_rules', label: 'Compliance Rules' },
  { value: 'aq_responses', label: 'Adequacy Questionnaire' },
  { value: 'suitability_conversion', label: 'Suitability Correspondence' },
  { value: 'agreement_users', label: 'Named RM' },
  { value: 'agreement_portfolios', label: 'Portfolio links' },
  { value: 'translation_terms', label: 'Translations' },
  { value: 'fee_schedules', label: 'Fee Schedule' },
];

const _CR_ENTITY_LABEL = new Map(CR_ENTITY_TYPE_OPTIONS.map((o) => [o.value, o.label]));

/** Friendly label for a CR `entity_type` (§3j). Falls back to a humanised
 *  Title-Case of the raw snake_case so an unmapped type never renders raw. */
export function crEntityLabel(entityType: string): string {
  return _CR_ENTITY_LABEL.get(entityType)
    ?? entityType.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export const CR_KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'RESTORE', label: 'Restore' },
];

export const changeRequestsApi = {
  async list(
    filters?: ChangeRequestsListFilters,
    limit = 50,
    offset = 0,
  ): Promise<Page<ChangeRequest>> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.entity_type) params.append('entity_type', filters.entity_type);
    // #47: arrays expand to repeated keys (FastAPI list parsing).
    if (filters?.entity_types) for (const t of filters.entity_types) params.append('entity_type', t);
    if (filters?.kinds) for (const k of filters.kinds) params.append('kind', k);
    if (filters?.requested_by_user_id) for (const u of filters.requested_by_user_id) params.append('requested_by_user_id', u);
    if (filters?.requested_from) params.append('requested_from', filters.requested_from);
    if (filters?.requested_to) params.append('requested_to', filters.requested_to);
    if (filters?.include_deleted) params.append('include_deleted', 'true');
    if (filters?.sort) params.append('sort', filters.sort);
    return api.get(`/change-requests?${params}`);
  },

  async get(id: UUID): Promise<ChangeRequest> {
    return api.get(`/change-requests/${id}`);
  },

  async approve(id: UUID, input?: ChangeRequestDecision): Promise<ChangeRequest> {
    return api.post(`/change-requests/${id}/approve`, input || {});
  },

  async reject(id: UUID, input?: ChangeRequestDecision): Promise<ChangeRequest> {
    return api.post(`/change-requests/${id}/reject`, input || {});
  },

  async cancel(id: UUID, input?: ChangeRequestDecision): Promise<ChangeRequest> {
    return api.post(`/change-requests/${id}/cancel`, input || {});
  },
};
